import { useCustom, type HttpError } from "@refinedev/core";
import type { WorkflowHistoryReport } from "../types";

interface UseWorkflowHistoryOptions {
  itemType: string;
  itemId: string;
  enabled?: boolean;
}

interface UseWorkflowHistoryResult {
  workflowHistory: WorkflowHistoryReport | null;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
  refetch: () => void;
}

/**
 * Hook to fetch workflow history report for an item.
 * Returns activities with their assignments, vote information, and comments.
 * Similar to Aras Innovator's "Workflow History Report".
 *
 * @param itemType - The exact Aras ItemType name (e.g., "Lead", "Opportunity")
 * @param itemId - The item's ID
 * @param enabled - Whether to enable the fetch
 */
export const useWorkflowHistory = ({
  itemType,
  itemId,
  enabled = true,
}: UseWorkflowHistoryOptions): UseWorkflowHistoryResult => {
  const shouldFetch = enabled && !!itemType && !!itemId;

  const {
    data: historyData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCustom<WorkflowHistoryReport>({
    // Use exact itemType name - no conversion
    url: `/items/${itemType}/${itemId}/workflow-history`,
    method: "get",
    queryOptions: {
      enabled: shouldFetch,
      staleTime: 60 * 1000, // 1 minute cache
      retry: 1, // Only retry once
    },
  });

  return {
    workflowHistory: historyData?.data || null,
    isLoading: shouldFetch && isLoading,
    isError,
    error: error || null,
    refetch,
  };
};
